import { useCallback, useEffect, useState } from 'preact/hooks';

type CountdownState = 'enabled' | 'disabled';

export const useCountdown = (time: number, defaultState: CountdownState) => {
  const [remainingTime, setRemainingTime] = useState(time);
  const [state, setState] = useState<CountdownState>(defaultState);

  const enable = useCallback(() => {
    setState('enabled');
  }, []);

  const disable = useCallback(() => {
    setState('disabled');
  }, []);

  const reset = useCallback(
    (newState: CountdownState) => {
      setRemainingTime(time);
      setState(newState);
    },
    [time]
  );

  useEffect(() => {
    if (state === 'disabled') {
      return;
    }

    setRemainingTime(time);

    let reference = Date.now();
    let requestId: number;

    const update = () => {
      setRemainingTime((prevRemainingTime) => {
        const elapsedInSeconds = (Date.now() - reference) / 1000;

        reference = Date.now();

        const newRemainingTime = Math.max(
          0,
          prevRemainingTime - elapsedInSeconds
        );

        if (newRemainingTime > 0) {
          requestId = requestAnimationFrame(update);
        }

        return newRemainingTime;
      });
    };

    requestId = requestAnimationFrame(update);

    return () => cancelAnimationFrame(requestId);
  }, [state, time]);

  return {
    remainingTime,
    enableCountdown: enable,
    disableCountdown: disable,
    resetCountdown: reset,
  };
};
